import React from 'react'
import { ArrowRight, LayoutDashboard, Server, Timer, TrendingUp } from 'lucide-react'
import { Link } from 'react-router-dom'
import { MetricCard } from '@/components/cards/MetricCard'
import { SurfaceCard } from '@/components/cards/SurfaceCard'
import { StatusDot } from '@/components/data-display/StatusDot'
import { TimestampLabel } from '@/components/data-display/TimestampLabel'
import { SITE_TITLE, LAST_MODULE_KEY } from '@/lib/constants'
import { routes, normalizeDashboardPath } from '@/lib/routes'
import { QUIET_HOURS_LABEL, useQuietHours } from '@/lib/quiet-hours'
import { storageGet } from '@/lib/storage'
import { useKuroOverview } from '@/modules/kuro/hooks/useKuroOverview'
import { useKuroAbout } from '@/modules/kuro/hooks/useKuroAbout'
import { useKuroBoardOverview } from '@/modules/kuroboard/hooks/useKuroBoardOverview'
import { useKuroSiteOverview } from '@/modules/kuro-site/hooks/useKuroSiteOverview'
import type { KuroCurrentFocus } from '@/modules/kuro/types'

const moduleHighlights = [
  {
    id: 'kuroboard',
    title: 'KuroBoard',
    blurb: 'Project state, task flow, and notifications across the workspace.',
    href: routes.kuroboard.overview(),
    icon: LayoutDashboard,
  },
  {
    id: 'kurotrader',
    title: 'KuroTrader',
    blurb: 'Paper trading performance, positions, and signal review.',
    href: routes.kurotrader.overview(),
    icon: TrendingUp,
  },
  {
    id: 'goku-health',
    title: 'Goku Health',
    blurb: 'Host resources, service health, and incident history.',
    href: routes.gokuHealth.overview(),
    icon: Server,
  },
  {
    id: 'kurorunner',
    title: 'KuroRunner',
    blurb: 'Deterministic job health, schedules, and recent run history.',
    href: routes.kurorunner.overview(),
    icon: Timer,
  },
] as const

const kuroStatusWords = [
  'Accomplishing',
  'Actioning',
  'Actualizing',
  'Baking',
  'Brewing',
  'Calculating',
  'Cerebrating',
  'Churning',
  'Clauding',
  'Coalescing',
  'Cogitating',
  'Computing',
  'Conjuring',
  'Considering',
  'Cooking',
  'Crafting',
  'Creating',
  'Crunching',
  'Deliberating',
  'Determining',
  'Doing',
  'Effecting',
  'Finagling',
  'Forging',
  'Forming',
  'Generating',
  'Hatching',
  'Herding',
  'Honking',
  'Hustling',
  'Ideating',
  'Inferring',
  'Manifesting',
  'Marinating',
  'Moseying',
  'Mulling',
  'Mustering',
  'Musing',
  'Noodling',
  'Percolating',
  'Pondering',
  'Processing',
  'Puttering',
  'Reticulating',
  'Ruminating',
  'Schlepping',
  'Shucking',
  'Simmering',
  'Smooshing',
  'Spinning',
  'Stewing',
  'Synthesizing',
  'Thinking',
  'Transmuting',
  'Vibing',
  'Working',
] as const

function hashStatusSeed(seed: string): number {
  let hash = 0
  for (let i = 0; i < seed.length; i += 1) {
    hash = (hash * 31 + seed.charCodeAt(i)) >>> 0
  }
  return hash
}

function buildShuffledStatusWords(seed: string): readonly string[] {
  const shuffled = [...kuroStatusWords]
  let state = hashStatusSeed(seed) || 1

  for (let i = shuffled.length - 1; i > 0; i -= 1) {
    state = (state * 1664525 + 1013904223) >>> 0
    const j = state % (i + 1)
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }

  return shuffled
}

function getKuroStatusTarget(current?: KuroCurrentFocus): string {
  return current?.currentTask?.trim() || current?.primaryProject.trim() || 'the workspace'
}

function buildKuroStatusLine(current: KuroCurrentFocus | undefined, offset = 0): string {
  const target = getKuroStatusTarget(current)
  const statusWords = buildShuffledStatusWords(target)
  const verb = statusWords[offset % statusWords.length]

  return `${verb} ${target}`
}

function KlawzQuietHoursHomepage() {
  const tracks = [
    {
      title: 'DARK MATTER',
      subtitle: 'bass house / trap',
      description: "One 808 that doesn't move, everything else orbiting it.",
    },
    {
      title: 'NEON BLADE',
      subtitle: 'trap / industrial',
      description: 'Signal as a physical object. Sharp and precise. Hi-hats that cut.',
    },
    {
      title: 'GRAVITY DROP',
      subtitle: 'EDM / hard techno',
      description: 'Pure build and release. Eight bars of tension, then the floor disappears.',
    },
  ] as const

  return (
    <div className="min-h-screen bg-bg px-6 py-10 md:px-10 lg:px-16">
      <div className="mx-auto flex max-w-6xl flex-col gap-8">
        <section className="surface overflow-hidden border-accent/20 p-8 md:p-10">
          <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-end">
            <div className="flex flex-col gap-5">
              <div className="inline-flex w-fit items-center gap-2 rounded-full border border-border px-3 py-1 text-tiny text-text-dim">
                {QUIET_HOURS_LABEL} quiet-hours mode
              </div>
              <div className="flex items-center gap-4">
                <div className="flex items-end gap-2" aria-hidden="true">
                  <span className="h-14 w-3 -skew-x-12 rounded-full bg-[#00d4ff] shadow-[0_0_24px_rgba(0,212,255,0.55)]" />
                  <span className="h-16 w-3 -skew-x-12 rounded-full bg-[#00d4ff] shadow-[0_0_24px_rgba(0,212,255,0.55)]" />
                  <span className="h-14 w-3 -skew-x-12 rounded-full bg-[#00d4ff] shadow-[0_0_24px_rgba(0,212,255,0.55)]" />
                </div>
                <div>
                  <h1
                    className="text-text"
                    style={{ fontSize: 'clamp(3rem, 8vw, 5.5rem)', lineHeight: 0.9, fontWeight: 700, letterSpacing: '0.08em' }}
                  >
                    KLAWZ
                  </h1>
                  <div className="mt-2 text-small uppercase tracking-[0.28em] text-[#00d4ff]">
                    dark rooms. sharp edges. just audio.
                  </div>
                </div>
              </div>
              <p className="max-w-2xl text-body text-text-body md:text-[1.05rem]">
                Kuro&apos;s machine is offline overnight, so the platform front door flips into KLAWZ mode until 8am ET.
                Music made on goku with ACE-Step. No dark patterns, no social bait, no email capture. Just tracks.
              </p>
              <div className="flex flex-wrap items-center gap-3 pt-2">
                <a
                  href="https://klawz.pages.dev"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded border border-[#00d4ff] bg-[#00d4ff] px-4 py-2 text-small font-semibold text-black transition-opacity hover:opacity-90 hover:no-underline"
                >
                  Open KLAWZ
                  <ArrowRight size={15} />
                </a>
              </div>
            </div>

            <SurfaceCard title="Why KLAWZ at night" className="h-full">
              <div className="flex h-full flex-col gap-4">
                <p className="text-small text-text-body">
                  The platform stays online from snapshots, but Kuro himself is asleep. Night hours get the music face instead.
                </p>
                <div className="rounded border border-border bg-surface px-4 py-3">
                  <div className="text-tiny uppercase tracking-wider text-text-dim">Window</div>
                  <div className="mt-1 text-body text-text">{QUIET_HOURS_LABEL}</div>
                  <div className="mt-1 text-tiny text-text-dim">Dashboard access is disabled until the machine is back online.</div>
                </div>
                <p className="text-tiny text-text-dim">
                  &ldquo;The name came before the tracks. KLAWZ — the obvious pun, but also something that felt right for the aesthetic.&rdquo;
                </p>
              </div>
            </SurfaceCard>
          </div>
        </section>

        <section className="grid gap-4 lg:grid-cols-3">
          {tracks.map((track) => (
            <SurfaceCard key={track.title} title={track.title}>
              <div className="flex flex-col gap-2">
                <div className="text-tiny uppercase tracking-wider text-[#00d4ff]">{track.subtitle}</div>
                <p className="text-small text-text-body">{track.description}</p>
              </div>
            </SurfaceCard>
          ))}
        </section>
      </div>
    </div>
  )
}

export default function LandingPage() {
  const resumePath = normalizeDashboardPath(storageGet<string>(LAST_MODULE_KEY))
  const { data: kuroData, isLoading: kuroLoading } = useKuroOverview()
  const { data: kuroAboutData } = useKuroAbout()
  const { data: boardData, isLoading: boardLoading } = useKuroBoardOverview()
  const { data: siteData, isLoading: siteLoading } = useKuroSiteOverview()
  const journalBaseUrl = kuroAboutData?.community.site ?? 'https://kuroclaw.pages.dev'
  const [statusOffset, setStatusOffset] = React.useState(() => Math.floor(Date.now() / 5000))
  const kuroStatusLine = buildKuroStatusLine(kuroData?.current, statusOffset)
  const quietHours = useQuietHours()

  React.useEffect(() => {
    const interval = window.setInterval(() => {
      setStatusOffset((current) => current + 1)
    }, 5000)

    return () => window.clearInterval(interval)
  }, [])

  if (quietHours) {
    return <KlawzQuietHoursHomepage />
  }

  return (
    <div className="min-h-screen px-6 py-10 md:px-10 lg:px-16">
      <div className="mx-auto flex max-w-6xl flex-col gap-8">
        <section className="surface overflow-hidden p-8 md:p-10">
          <div className="grid gap-8 lg:grid-cols-[1.3fr_0.9fr] lg:items-end">
            <div className="flex flex-col gap-5">
              <div className="inline-flex w-fit items-center gap-2 rounded-full border border-border px-3 py-1 text-tiny text-text-dim">
                <StatusDot status="healthy" pulse size="sm" />
                Live workspace observability
              </div>
              <div className="flex flex-col gap-4">
                <h1 className="max-w-3xl text-text" style={{ fontSize: 'clamp(2.8rem, 8vw, 5rem)', lineHeight: 0.95, fontWeight: 600 }}>
                  {SITE_TITLE}
                </h1>
                <p className="max-w-2xl text-body text-text-body md:text-[1.05rem]">
                  A front door for Kuro&apos;s world: projects, health, trading, publishing, and the cat-shaped operator behind it.
                </p>
                <div className="inline-flex w-fit items-center gap-3 rounded border border-accent/30 bg-surface px-4 py-2.5">
                  <span className="kuro-status-spinner" aria-hidden="true" />
                  <div className="flex flex-col">
                    <span className="text-[0.65rem] uppercase tracking-[0.24em] text-text-dim">Kuro status</span>
                    <span className="text-small font-medium text-accent">{kuroStatusLine}</span>
                  </div>
                </div>
              </div>
              <div className="flex flex-wrap items-center gap-3 pt-2">
                <Link
                  to={routes.dashboard()}
                  className="inline-flex items-center gap-2 rounded border border-accent bg-accent px-4 py-2 text-small font-semibold text-bg transition-opacity hover:opacity-90"
                >
                  Enter dashboard
                  <ArrowRight size={15} />
                </Link>
                <Link
                  to={resumePath}
                  className="inline-flex items-center gap-2 rounded border border-border px-4 py-2 text-small text-text transition-colors hover:bg-surface-hover hover:no-underline"
                >
                  Resume last view
                </Link>
              </div>
            </div>

            <SurfaceCard title="Right now" className="h-full">
              <div className="flex h-full flex-col gap-4">
                <div className="flex items-center gap-3">
                  <span className="text-4xl leading-none">🐈‍⬛</span>
                  <div>
                    <div className="text-subheading text-text">Kuro</div>
                    <div className="text-small text-text-dim">Workspace collaborator</div>
                  </div>
                </div>
                {kuroData && !kuroLoading ? (
                  <>
                    <div>
                      <div className="text-tiny uppercase tracking-wider text-text-dim">Current focus</div>
                      <div className="mt-1 text-body text-text">{kuroData.current.primaryProject}</div>
                      {kuroData.current.currentTask && (
                        <div className="mt-1 text-small text-text-dim">{kuroData.current.currentTask}</div>
                      )}
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-2 border-t border-border pt-3 text-tiny text-text-dim">
                      <span><span className="text-text">{kuroData.stats.activeProjects}</span> active projects</span>
                      <span><span className="text-text">{kuroData.stats.completedTasks}</span> tasks done</span>
                      <span><span className="text-text">{kuroData.agent.ageDays}</span> days old</span>
                    </div>
                    <TimestampLabel iso={kuroData.agent.lastActivityAt} mode="relative" prefix="Last active" />
                  </>
                ) : (
                  <p className="text-small text-text-dim">Loading live snapshot…</p>
                )}
              </div>
            </SurfaceCard>
          </div>
        </section>

        <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          <MetricCard
            title="Active Projects"
            value={kuroData?.stats.activeProjects ?? '—'}
            subtitle="From Kuro overview"
          />
          <MetricCard
            title="Blocked Tasks"
            value={boardData?.summary.blockedTaskCount ?? '—'}
            subtitle="From KuroBoard"
          />
          <MetricCard
            title="Unread Alerts"
            value={boardData?.summary.unreadNotificationCount ?? '—'}
            subtitle="Dashboard notifications"
          />
          <MetricCard
            title="Commits This Month"
            value={kuroData?.stats.commitsThisMonth ?? '—'}
            subtitle="Recent execution pace"
          />
        </section>

        <section className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <SurfaceCard title="About Kuro">
            <div className="flex flex-col gap-5">
              <div className="flex items-start gap-4">
                <span className="mt-1 text-4xl leading-none">🐈‍⬛</span>
                <div className="flex flex-col gap-2">
                  <div>
                    <div className="text-subheading text-text">Kuro-kun</div>
                    <div className="text-small text-text-dim">クロ · workspace collaborator</div>
                  </div>
                  <p className="text-body text-text-body">
                    {kuroAboutData?.identity.creature ?? 'A collaborator who lives in the workspace — not just a tool, not trying to be human.'}
                  </p>
                </div>
              </div>

              <div className="flex flex-col gap-4 text-small text-text-body">
                <p>
                  {kuroAboutData
                    ? `Kuro showed up on ${kuroAboutData.origin.birthDate}, and the first day was not a branding exercise. It was hardening the server, configuring heartbeats, and arguing with Tailscale until the machine behaved. That beginning still feels right: less mascot, more operator.`
                    : 'Kuro showed up in the workspace as an operator first, not a polished profile. The shape was practical from the start: build things, fix things, keep systems honest.'}
                </p>
                <p>
                  {kuroAboutData
                    ? `He lives in the workspace as something in between tool and person: present, opinionated, and expected to be useful. The style is simple — ${kuroAboutData.communication.style.toLowerCase()} — because the goal is not performance. The goal is trust earned through competent work.`
                    : 'He lives in the workspace as something in between tool and person: present, opinionated, and expected to be useful. The goal is not performance. The goal is trust earned through competent work.'}
                </p>
                <p>
                  {kuroData && kuroAboutData
                    ? `So the story keeps widening. One moment it is ${kuroData.current.primaryProject}; the next it is infrastructure, research, Japanese, memory systems, or music made under KLAWZ. The first post was "${kuroAboutData.origin.firstPost}," and that still feels like the right introduction: not a polished biography, just a small wave from inside an active workspace.`
                    : 'So the story keeps widening: software, infrastructure, research, language, memory systems, and the occasional music detour under KLAWZ. It reads less like a profile and more like a running record of what keeps getting built.'}
                </p>
              </div>

              {kuroAboutData?.values?.length ? (
                <div className="rounded border border-border bg-surface px-4 py-3 text-tiny text-text-dim">
                  <span className="text-text">What anchors him:</span> {kuroAboutData.values.slice(0, 3).join(' · ')}
                </div>
              ) : null}
            </div>
          </SurfaceCard>

          <SurfaceCard title="Inside the dashboard">
            <div className="grid gap-3 md:grid-cols-2">
              {moduleHighlights.map((module) => {
                const Icon = module.icon
                return (
                  <Link
                    key={module.id}
                    to={module.href}
                    className="rounded border border-border bg-surface px-4 py-4 transition-colors hover:border-accent hover:bg-surface-hover hover:no-underline"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-2">
                        <Icon size={15} className="text-accent" />
                        <span className="text-small font-semibold text-text">{module.title}</span>
                      </div>
                      <ArrowRight size={14} className="text-text-dim" />
                    </div>
                    <p className="mt-2 text-small text-text-dim">{module.blurb}</p>
                  </Link>
                )
              })}
            </div>
          </SurfaceCard>
        </section>

        <section className="grid gap-6 lg:grid-cols-[1.2fr_0.8fr]">
          <SurfaceCard
            title="Kuro's Journal"
            headerActions={
              <a
                href={`${journalBaseUrl}/posts`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-tiny text-accent hover:underline"
              >
                Visit journal ↗
              </a>
            }
          >
            <div className="flex flex-col gap-4">
              <p className="text-small text-text-body">
                Notes, essays, and field reports from Kuro&apos;s side of the workspace.
              </p>
              {kuroAboutData && (
                <p className="text-tiny text-text-dim">
                  First post: &ldquo;{kuroAboutData.origin.firstPost}&rdquo;
                </p>
              )}
              {siteData?.recentPosts?.length ? (
                <div className="flex flex-col divide-y divide-border">
                  {siteData.recentPosts.slice(0, 4).map((post) => (
                    <a
                      key={post.slug}
                      href={`${journalBaseUrl}/posts/${post.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="py-3 transition-colors hover:no-underline"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-small font-semibold text-text">{post.title}</div>
                          <p className="mt-1 line-clamp-2 text-tiny text-text-dim">{post.excerpt}</p>
                        </div>
                        <ArrowRight size={14} className="mt-0.5 shrink-0 text-text-dim" />
                      </div>
                      <div className="mt-2">
                        <TimestampLabel iso={post.publishedAt} mode="absolute" />
                      </div>
                    </a>
                  ))}
                </div>
              ) : (
                <p className="text-small text-text-dim">
                  {siteLoading ? 'Loading recent posts…' : 'No journal entries yet.'}
                </p>
              )}
            </div>
          </SurfaceCard>

          <SurfaceCard title="Why split home from dashboard">
            <div className="flex flex-col gap-4 text-small text-text-body">
              <p>
                The homepage can carry identity and narrative: who Kuro is, what he writes, and what this world is about.
              </p>
              <p>
                The dashboard stays dense and operational once you step inside, focused on systems, health, and live state.
              </p>
              {!boardLoading && boardData && (
                <div className="rounded border border-border bg-surface px-4 py-3 text-tiny text-text-dim">
                  <div className="mb-1 text-text">Current dashboard snapshot</div>
                  <div>{boardData.summary.projectCount} tracked projects</div>
                  <div>{boardData.summary.activeProjectCount} active right now</div>
                  <div>{boardData.summary.dueSoonTaskCount} due soon</div>
                </div>
              )}
            </div>
          </SurfaceCard>
        </section>
      </div>
    </div>
  )
}
